import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";
import { Bar } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components and zoom plugin used by this chart.
ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  zoomPlugin
);


function ChargesPerDayChart({
  title,
  data = [],
  grouping = "daily"
}) {
  const chartRef = useRef(null);

  // Stores charging counts grouped by location.
  const groupedByLocation = {};


  const chartColours = [
    "#2e7d32",
    "#f9a825",
    "#1565c0",
    "#8e24aa",
    "#ef6c00",
    "#00897b",
    "#c62828",
    "#6d4c41",
    "#5e35b1",
    "#0277bd",
    "#558b2f",
    "#ad1457"
  ];


  // Validate the API data and group each point by charging location.
  data.forEach((row) => {
    const date = new Date(row.date);
    const value = Number(row.value);

    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() < 2016 ||
      Number.isNaN(value)
    ) {
      return;
    }

    const location =
      row.locationName ||
      row.chargingStationId ||
      "Unknown location";

    if (!groupedByLocation[location]) {
      groupedByLocation[location] = [];
    }

    groupedByLocation[location].push({
      x: date.getTime(),
      y: value
    });
  });


  // Create one dataset for each charging location.
  const datasets = Object.keys(groupedByLocation).map((location, index) => {
    const colour = chartColours[index % chartColours.length];

    return {
      label: location,
      data: groupedByLocation[location],
      backgroundColor: colour,
      borderColor: colour,
      borderWidth: 1,

      // Allow the bars to use more of the available horizontal space.
      // maxBarThickness prevents them becoming excessively wide when
      // only a small number of dates are displayed.
      barPercentage: 0.95,
      categoryPercentage: 0.9,
      maxBarThickness:
        grouping === "daily"
          ? 24
          : grouping === "monthly"
            ? 42
            : 55
    };
  });


  // Match the x-axis time scale to the selected grouping.
  const getTimeUnit = () => {
    if (grouping === "monthly") return "month";
    if (grouping === "yearly") return "year";

    return "day";
  };


  const chartData = {
    datasets
  };


  // Configure the stacked chart, time axis and zoom behaviour.
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    scales: {
      x: {
        type: "time",
        stacked: true,
        time: {
          unit: getTimeUnit()
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 20
        },
        title: {
          display: true,
          text: "Date"
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Charges"
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: {
          boxWidth: 14,
          boxHeight: 14,
          padding: 14
        }
      },
      tooltip: {
        mode: "index",
        intersect: false
      },

      // Allow horizontal zooming and panning across the date range.
      zoom: {
        limits: {
          x: {
            min: "original",
            max: "original",
            minRange:
              grouping === "yearly"
                ? 31_536_000_000
                : grouping === "monthly"
                  ? 2_592_000_000
                  : 86_400_000
          }
        },
        pan: {
          enabled: true,
          mode: "x",
          modifierKey: "shift"
        },
        zoom: {
          mode: "x",
          wheel: {
            enabled: true,
            modifierKey: "ctrl",
            speed: 0.08
          },
          pinch: {
            enabled: true
          },
          drag: {
            enabled: true,
            modifierKey: "shift",
            borderWidth: 1
          }
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>{title}</h2>

          <p>
            Hold Ctrl and scroll to zoom, or hold Shift and drag to
            select an area.
          </p>
        </div>

        <ChartToolbar
          chartRef={chartRef}
          title={title}
        />
      </div>


      {/* Render the stacked charges-per-day bar chart */}
      <div className="chart-canvas-container">
        <Bar
          ref={chartRef}
          data={chartData}
          options={options}
        />
      </div>
    </article>
  );
}


export default ChargesPerDayChart;